import { useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import MobileCarouselDots from "@/Common/MobileCarouselDots";
import { useDesktopSlides } from "@/hooks/useIsDesktop";
import useMobileScrollDots from "@/hooks/useMobileScrollDots";
import useSlickArrows from "@/hooks/useSlickArrows";
import styles from "./TopDeals.module.css";
import { fetchSeasonalDeals } from "@/services/api/common";
import { withRegionPath } from "@/utils/session";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { FiArrowUpRight } from "react-icons/fi";

function ArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3 8h10M9 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DealCard({ deal }) {
  return (
    <a href={withRegionPath(`/deals/${deal.slug}`)} className={styles.dealCard}>
      <div className={styles.imageWrap}>
        <img
          src={deal.webPageThumbnail}
          alt={deal.webPageThumbnailAlt || deal.pageTitle}
          className={styles.cardImage}
        />
        <div className={styles.overlay} />
        <div className={styles.content}>
          <h3>{deal.pageTitle}</h3>
          {deal.pageSubTitle ? <p>{deal.pageSubTitle}</p> : null}
        </div>
        <div className={styles.cardArrow} aria-hidden="true">
          <FiArrowUpRight size={18} />
        </div>
      </div>
      <h3 className={styles.mobileLabel}>{deal.pageTitle}</h3>
    </a>
  );
}

export default function TopDeals({ initialSeasonalDeals = null }) {
  const [seasonalDeals, setSeasonalDeals] = useState(initialSeasonalDeals ?? []);
  const [isLoaded, setIsLoaded] = useState(initialSeasonalDeals != null);
  const { isDesktop, mounted, slidesToShow } = useDesktopSlides();
  const sliderRef = useRef(null);
  useEffect(() => {
    if (initialSeasonalDeals != null) return;

    fetchSeasonalDeals()
      .then((response) => {
        setSeasonalDeals(Array.isArray(response?.data) ? response.data : []);
      })
      .catch((error) => {
        console.error("Failed to load seasonal deals:", error);
        setSeasonalDeals([]);
      })
      .finally(() => {
        setIsLoaded(true);
      });
  }, [initialSeasonalDeals]);

  const { canPrev, canNext, slickArrowEvents } = useSlickArrows(
    seasonalDeals.length,
    slidesToShow
  );

  const slickSettings = {
    dots: false,
    infinite: false,
    centerMode: false,
    speed: 400,
    slidesToShow,
    slidesToScroll: 1,
    arrows: false,
    swipe: false,
    draggable: false,
    touchMove: false,
    ...slickArrowEvents,
  };

  const showSlick = mounted && isDesktop;
  const fewSlides = seasonalDeals.length <= slidesToShow;
  const hasItems = seasonalDeals.length > 0;
  const showEmpty = isLoaded && !hasItems;
  const { scrollRef, activeDot, mobileDots, onScroll, goToDot } = useMobileScrollDots(
    seasonalDeals.length,
    seasonalDeals.map((deal) => deal.slug).join("|")
  );

  return (
    <section className={`${styles.dealsSection} standard-padding`}>
      <div className={styles.header}>
        <h2 className="standard-heading">
          <span className={styles.titleDesktop}>Exclusive Deals</span>
          <span className={styles.titleMobile}>Exclusive Deals</span>
        </h2>

        {hasItems ? (
          <div className="headerArrows">
          <button
            type="button"
            className="headerArrowBtn"
            aria-label="Previous"
            disabled={!canPrev}
            onClick={() => {
              if (typeof sliderRef.current?.slickPrev === "function") {
                sliderRef.current.slickPrev();
              }
            }}
          >
            <MdChevronLeft size={22} />
          </button>
          <button
            type="button"
            className="headerArrowBtn"
            aria-label="Next"
            disabled={!canNext}
            onClick={() => {
              if (typeof sliderRef.current?.slickNext === "function") {
                sliderRef.current.slickNext();
              }
            }}
          >
            <MdChevronRight size={22} />
          </button>
        </div>
        ) : null}

        {hasItems ? (
          <a href={withRegionPath("/deals")} className={styles.viewAll}>
            View All
            <ArrowIcon />
          </a>
        ) : null}
      </div>

      {showEmpty ? (
        <p className="nodata">No data found</p>
      ) : hasItems && showSlick ? (
        <div className={`${styles.slickWrap} ${fewSlides ? styles.alignLeft : ""}`}>
          <Slider ref={sliderRef} {...slickSettings}>
            {seasonalDeals.map((deal) => (
              <div key={deal.slug} className={styles.slickSlide}>
                <DealCard deal={deal} />
              </div>
            ))}
          </Slider>
        </div>
      ) : hasItems ? (
        <>
          <div ref={scrollRef} className={styles.slider} onScroll={onScroll}>
            {seasonalDeals.map((deal) => (
              <DealCard key={deal.slug} deal={deal} />
            ))}
          </div>
          <MobileCarouselDots
            count={mobileDots}
            activeIndex={activeDot}
            onSelect={goToDot}
          />
        </>
      ) : null}
    </section>
  );
}
